/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {afterNextRender, Component, computed, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {CellEditingStoppedEvent} from 'ag-grid-community';
import {IoMatrixWorkflowComponent} from '../io-matrix-workflow.component';
import {
    buildIoMatrixSnapshotOptions,
    formatIoMatrixScopeLabel,
    IcdTemplateInventory,
    IO_MATRIX_ORIGINAL_VARIANT,
    IO_MATRIX_ORIGINAL_VARIANT_LABEL,
    IoMatrixSnapshot,
    IoMatrixView,
    normalizeIcdTemplateInventory,
    normalizeIoMatrixScopeField
} from '../../../model/io-matrix.models';
import {forkJoin, Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';

type TemplateMode = 'edit' | 'create' | null;

@Component({
  selector: 'app-io-matrix-icds',
  templateUrl: './io-matrix-icds.component.html',
  styleUrls: ['./io-matrix-icds.component.scss', '../io-matrix-workflow.component.scss'],
  standalone: false
})
export class IoMatrixIcdsComponent extends IoMatrixWorkflowComponent {
  get selectedView(): IoMatrixView {
    return this.selectedViewSignal();
  }

  set selectedView(value: IoMatrixView) {
    this.selectedViewSignal.set(value ?? 'architecture');
    this.applyIoMatrixViewVisibility();
  }

  private readonly programQuerySignal = signal('');
  private readonly savedMatrixQuerySignal = signal('');
  private readonly existingTemplateQuerySignal = signal('');
  private templateColumnsBaseline: {
    field: string;
    program: string;
    modelYear: string;
    rpo: string;
    additionalRpos: string;
    buildPhase: string;
    variant: string;
  }[] = [];
  /** Full saved-matrix rows from the API; used for variant token discovery (AC7). */
  private readonly allMatrixRowsSignal = signal<any[]>([]);
  /** Connector/pin-assigned subset; template grid and save paths use this list. */
  private readonly allConnectorRowsSignal = signal<any[]>([]);
  readonly matrixVariantOptions = computed(() => {
    const tokens = this.collectDistinctVariantTokens(this.allMatrixRowsSignal());
    return [
      {label: IO_MATRIX_ORIGINAL_VARIANT_LABEL, value: IO_MATRIX_ORIGINAL_VARIANT},
      ...tokens.map(token => ({label: token, value: token}))
    ];
  });
  readonly showVariantDropdown = computed(() =>
    this.collectDistinctVariantTokens(this.allMatrixRowsSignal()).length > 0
  );
  private readonly selectedVariantSignal = signal(IO_MATRIX_ORIGINAL_VARIANT);
  private templateRowsBaseline: any[] = [];
  private templateDetailCache = new Map<number, Map<number, string>>();
  private templateDetailLoading = new Set<number>();
  private savedMatrixSelectionRequestId = 0;
  // `savedMatrixOptions` is signal-backed so the dropdown UX matches the IO Matrix page
  // (`io-matrix.component.ts`) while keeping change-detection updates scheduled through
  // Angular's reactive system. The exposed getter/setter pair below preserves field-style
  // access so existing tests and template `[(ngModel)]`-style consumers keep working.
  private readonly savedMatrixOptionsSignal =
    signal<{ label: string; key: string; snapshot: IoMatrixSnapshot }[]>([]);
  selectedMatrixKey = '';
  selectedMatrixLabel = '';
  templateMode: TemplateMode = null;
  editableTemplateOptions: { label: string; value: string }[] = [];
  selectedExistingTemplateKey: string | null = null;
  selectedExistingTemplateLabel = '';
  programOptionsSignal = signal<string[]>([]);
  modelYearOptions: string[] = [];
  selectedProgramForNewTemplate = '';
  selectedModelYearForNewTemplate = '';
  selectedRpoForNewTemplate = '';
  selectedAdditionalRposForNewTemplate = '';
  selectedBuildPhaseForNewTemplate = '';
  templateCadenceUpgradeAvailable = false;
  templateDeviceIoUpgradeWarning = false;
  templateLatestCadenceAlreadyExistsWarning = false;
  templateTargetCadenceVer = '';
  latestMatrixCadenceVer = '';
  copyDialogVisible = false;
  copySourceColumnField = '';
  copyTargetProgram = '';
  copyTargetModelYear = '';
  copyTargetRpo = '';
  copyTargetAdditionalRpos = '';
  copyTargetBuildPhase = '';
  private readonly copyTargetProgramQuerySignal = signal('');

  get selectedVariant(): string {
    return this.selectedVariantSignal();
  }

  set selectedVariant(value: string) {
    this.selectedVariantSignal.set(this.normalizeTemplateVariant(value));
  }

  protected override getSelectedTemplateVariant(): string {
    return this.normalizeTemplateVariant(this.selectedVariant);
  }

  onVariantSelectionChanged(): void {
    this.clearTemplateColumnsOnVariantChange();
    this.applySelectedVariantToRows();
    this.refreshTemplateSelectionOptions();
  }

  override ngOnInit(): void {
    // Deliberately do NOT call `super.ngOnInit()` here. The parent's `ngOnInit` runs
    // `applyMergeCellPresentation(this.cols)` and `loadObjectCodes()` which are scoped to the
    // IO-matrix-edit page (they configure `cols` for the matrix grid and populate the OC
    // dropdown). On the ICDs page we use `templateCols` not `cols`, and OC selection is
    // driven by the saved-matrix dropdown rather than a free OC picker, so calling super
    // would either re-bind a hidden grid or fire an unused circuit-options request.
    // We also explicitly add ICDs-specific bootstrap (`checkExportPermission`,
    // `loadSavedIoMatrices`, `loadProgramAndModelYearReferenceOptions`).
    afterNextRender(() => {
      this.permission.set(false);
      this.esciService.getPermissionAndFileSizeLimit(this, false);
      this.esciService.checkExportPermission(this);
      this.rebuildTemplateColumns();
      this.loadTemplateInventory();
      this.loadSavedIoMatrices();
      this.loadProgramAndModelYearReferenceOptions();
    }, {injector: this.injector});
  }

  protected override getHistoryDownloadRequest(): Observable<ArrayBuffer> | null {
    if (!this.objectCode || !this.architecture) {
      this.messageService.processError('Select a saved IO Matrix first.');
      return null;
    }
    return this.esciService.getIcdTemplateHistory(
      this.objectCode,
      this.objectCodeInst,
      this.architecture,
      this.cadenceVer || undefined
    );
  }

  protected override getReportFileNamePrefix(): string {
    return 'IcdIoMatrixTemplatesReport';
  }

  protected override getHistoryFileNamePrefix(): string {
    return 'IcdIoMatrixTemplatesHistory';
  }

  loadSavedIoMatrices(): void {
    this.esciService.getIoMatrixSnapshots().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (snapshots) => {
        this.savedMatrixOptions = buildIoMatrixSnapshotOptions(snapshots);
        this.syncSelectedMatrixKeyFromResolvedSnapshot();
        this.syncSelectedMatrixLabelFromKey();
        this.refreshTemplateSelectionOptions();
      },
      error: () => {
        this.savedMatrixOptions = [];
        this.selectedMatrixLabel = '';
        this.messageService.processError('Failed to load saved IO Matrix options.');
      }
    });
  }

  filterSavedMatrixSuggestions(event: { query?: string }): void {
    this.savedMatrixQuerySignal.set(event?.query ?? '');
  }

  onSavedMatrixSelected(): void {
    const selectedOption = this.savedMatrixOptions
      .find(option => option.label === this.selectedMatrixLabel);
    this.selectedMatrixKey = selectedOption?.key || '';
    this.onSavedMatrixChanged();
  }

  onSavedMatrixCleared(): void {
    this.selectedMatrixLabel = '';
    this.selectedMatrixKey = '';
    this.savedMatrixQuerySignal.set('');
    this.onSavedMatrixChanged();
  }

  get savedMatrixOptions(): { label: string; key: string; snapshot: IoMatrixSnapshot }[] {
    return this.savedMatrixOptionsSignal();
  }

  set savedMatrixOptions(value: { label: string; key: string; snapshot: IoMatrixSnapshot }[]) {
    this.savedMatrixOptionsSignal.set(value ?? []);
  }

  // Mirrors `IoMatrixComponent.filteredSavedIoMatrixSuggestions` so the IO Matrix ICDs
  // dropdown supports both quick-click (empty-query dropdown) AND type-to-filter, exactly
  // like the IO Matrix page. PrimeNG `p-autoComplete` clears its loading spinner only when
  // its `set suggestions(value)` setter fires, which (per Angular's identity check on
  // non-primitive inputs) requires a NEW array reference. A `.map()`/`.filter()` getter
  // returns a fresh array on every read and is the simplest pattern that satisfies that
  // contract; it is intentionally NOT a memoized `computed` because memoization caches the
  // reference and starves PrimeNG of the change signal it needs.
  get filteredSavedMatrixOptions(): string[] {
    const query = (this.savedMatrixQuerySignal() || '').trim().toLowerCase();
    const labels = this.savedMatrixOptionsSignal().map(option => option.label);
    if (!query) {
      return labels;
    }
    return labels.filter(label => (label || '').toLowerCase().includes(query));
  }

  override loadTemplateInventory(): void {
    this.esciService.getIcdTemplateInventory().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: rows => {
        // 200 OK with [] is a normal initial state - we do NOT toast here.
        this.templateInventory = this.normalizeTemplateInventoryRows(rows);
        this.refreshTemplateSelectionOptions();
      },
      error: (err) => {
        // Explicit "request failed" toast separate from the empty-data path above.
        this.messageService.processError(
          err?.error?.message
            || 'Could not load ICD template inventory (request failed). Try again or contact support.');
        this.templateInventory = [];
      }
    });
  }

  onSavedMatrixChanged(): void {
    const requestId = ++this.savedMatrixSelectionRequestId;
    const selected = this.savedMatrixOptions.find(option => option.key === this.selectedMatrixKey)?.snapshot;
    if (!selected) {
      this.selectedMatrixLabel = '';
      this.clearMatrixContext();
      return;
    }
    this.selectedMatrixLabel = this.savedMatrixOptions.find(option => option.key === this.selectedMatrixKey)?.label || '';
    this.objectCode = selected.objectCode;
    this.objectCodeInst = normalizeIoMatrixScopeField(selected.objectCodeInst);
    this.setObjectCodeInstSelected(true);
    this.architecture = selected.architecture;
    this.cadenceVer = selected.cadenceVer;
    this.templateRows = [];
    this.ioMatrixRows = [];
    this.templateColumns = [];
    this.rebuildTemplateColumns();
    this.templateMode = null;
    this.selectedExistingTemplateKey = null;
    this.selectedExistingTemplateLabel = '';
    this.existingTemplateQuerySignal.set('');
    this.templateCadenceUpgradeAvailable = false;
    this.templateLatestCadenceAlreadyExistsWarning = false;
    this.templateTargetCadenceVer = '';
    this.latestMatrixCadenceVer = selected.cadenceVer;
    this.templateDeviceIoUpgradeWarning = false;
    this.inlineMergeOriginalValueDisplay = false;
    this.cadenceMergePreviewActive = false;
    this.cadenceMergeSourceCadenceVer = '';
    this.evaluateCadenceUpgradeSignals(selected, undefined);
    // Surface the same ref-mapping awareness users get on the IO Matrix edit page:
    // template editing / saving is allowed here, but downstream ICD creation will be
    // blocked until a Reference Mapping exists for this OC / OCI.
    this.refreshRefMapWarning();

    this.esciService.getIoMatrixRows(selected.matrixId, selected.cadenceVer).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: response => {
        if (requestId !== this.savedMatrixSelectionRequestId) {
          return;
        }
        const loadedRows = response?.rows || [];
        this.allMatrixRowsSignal.set(loadedRows);
        this.allConnectorRowsSignal.set(this.filterRowsWithAssignedConnectorAndPin(loadedRows));
        this.selectedVariant = IO_MATRIX_ORIGINAL_VARIANT;
        this.applySelectedVariantToRows();
        this.initializeTemplateChangeTracking();
        this.refreshTemplateSelectionOptions();
      },
      error: () => {
        if (requestId !== this.savedMatrixSelectionRequestId) {
          return;
        }
        this.messageService.processError('Failed to load saved IO Matrix rows.');
      }
    });
  }

  chooseTemplateMode(mode: Exclude<TemplateMode, null>): void {
    if (!this.selectedMatrixKey) {
      this.messageService.processError('Select a saved IO Matrix first.');
      return;
    }
    this.templateMode = mode;
    this.selectedExistingTemplateKey = null;
    this.selectedExistingTemplateLabel = '';
    this.existingTemplateQuerySignal.set('');
    this.templateCadenceUpgradeAvailable = false;
    this.templateLatestCadenceAlreadyExistsWarning = false;
    this.templateTargetCadenceVer = '';
    this.selectedProgramForNewTemplate = '';
    this.selectedModelYearForNewTemplate = '';
    this.selectedRpoForNewTemplate = '';
    this.selectedAdditionalRposForNewTemplate = '';
    this.selectedBuildPhaseForNewTemplate = '';
    this.programQuerySignal.set('');
    if (mode === 'edit') {
      // Always reload from the server when entering edit mode so a template saved in create
      // mode (or by another user) is visible immediately in the Existing ICD Template dropdown.
      this.refreshTemplateSelectionOptions();
      this.loadTemplateInventory();
      return;
    }
    this.refreshTemplateSelectionOptions();
  }

  onExistingTemplateChanged(): void {
    const selectedMatrix = this.resolveSelectedMatrixSnapshot();
    const selectedTemplate = this.resolveSelectedExistingTemplate();
    if (!selectedMatrix || !selectedTemplate) {
      this.templateCadenceUpgradeAvailable = false;
      this.templateLatestCadenceAlreadyExistsWarning = false;
      this.templateTargetCadenceVer = '';
      return;
    }
    this.evaluateCadenceUpgradeSignals(selectedMatrix, selectedTemplate);
  }

  filterExistingTemplateSuggestions(event: { query?: string }): void {
    this.existingTemplateQuerySignal.set(event?.query ?? '');
  }

  onExistingTemplateSelected(): void {
    const selectedOption = this.editableTemplateOptions
      .find(option => option.label === this.selectedExistingTemplateLabel);
    this.selectedExistingTemplateKey = selectedOption?.value || null;
    this.onExistingTemplateChanged();
  }

  onExistingTemplateCleared(): void {
    this.selectedExistingTemplateLabel = '';
    this.selectedExistingTemplateKey = null;
    this.existingTemplateQuerySignal.set('');
    this.onExistingTemplateChanged();
  }

  get filteredExistingTemplateOptions(): string[] {
    const query = (this.existingTemplateQuerySignal() || '').trim().toLowerCase();
    const labels = this.editableTemplateOptions.map(option => option.label);
    if (!query) {
      return labels;
    }
    return labels.filter(label => (label || '').toLowerCase().includes(query));
  }

  addTemplateColumnFromSelection(): void {
    if (!this.templateMode) {
      this.messageService.processError('Choose Edit or Create New template mode first.');
      return;
    }
    if (!this.templateRows.length) {
      this.messageService.processError('Select a saved IO Matrix first.');
      return;
    }
    if (this.templateMode === 'edit') {
      this.addColumnFromExistingTemplate();
      return;
    }
    this.addColumnForNewTemplate();
  }

  clearTemplateColumns(): void {
    if (!this.templateColumns.length) {
      return;
    }
    this.templateColumns = [];
    this.templateRows = (this.templateRows || []).map(row => this.stripTemplateColumnValues(row));
    this.rebuildTemplateColumns();
    this.dataChanged = true;
  }

  override cellEditingStopped(event: CellEditingStoppedEvent<any>, rows: any[]): void {
    // Align with base `EditableGridComponent.cellEditingStopped` behavior. Do NOT short-circuit
    // on event value equality (`event.value === event.oldValue`): dropdown editors can report
    // identical event values while still mutating row data, and we must keep Save/tooltip/styling
    // consistent (see editable-grid-component.ts comment). Returning early there caused dropdown
    // template-column re-selections of the same value to lose change tracking.
    const field = event.colDef?.field || '';
    const row: any = event.data;
    if (!row || !field) {
      return;
    }

    row.changes = row.changes || new Map<string, string>();
    const originalValue = row.originalRowData ? (row.originalRowData[field] ?? '') : '';
    const proposedValue = event.value ?? row[field] ?? '';
    const currentValue = this.isTemplateField(field)
      ? this.normalizeTemplateCircuitSelection(proposedValue)
      : proposedValue;
    row[field] = currentValue;
    if (JSON.stringify(currentValue) !== JSON.stringify(originalValue)) {
      row.changes.set(field, `Original Value: ${originalValue}`);
    } else {
      row.changes.delete(field);
    }

    row.state = row.changes.size > 0 ? 'changed' : 'saved';
    this.dataChanged = (rows || []).some(r => r?.state !== 'saved');
    // Template grid rows do not carry ValidationModel fieldRules metadata.
    // Run shared validation only when rule metadata is present.
    const hasValidationRules = (rows || []).some(r => Array.isArray(r?.fieldRules));
    if (hasValidationRules) {
      this.areRowsValid(rows as any);
    } else {
      this.duplicate = false;
      this.rowsInvalid = false;
    }

    if (this.isGridApiAlive(this.gridApi)) {
      this.gridApi.refreshCells({force: true});
      this.gridApi.redrawRows();
    }
  }

  resetTemplateChanges(): void {
    // Bracket the reset path with `resetting = true/false` so the spinner template bindings
    // activate while the work runs. Wrap in try/finally so the flag clears even if grid
    // refresh throws.
    this.resetting = true;
    try {
      if (!this.templateRowsBaseline.length && !this.templateColumnsBaseline.length) {
        this.dataChanged = false;
        this.duplicate = false;
        this.rowsInvalid = false;
        return;
      }
      this.templateColumns = structuredClone(this.templateColumnsBaseline);
      this.rebuildTemplateColumns();
      this.templateRows = this.prepareTemplateRowsForTracking(structuredClone(this.templateRowsBaseline));
      this.dataChanged = false;
      this.duplicate = false;
      this.rowsInvalid = false;
      if (this.isGridApiAlive(this.gridApi)) {
        this.gridApi.refreshCells({force: true});
        this.gridApi.redrawRows();
      }
    } finally {
      this.resetting = false;
    }
  }

  override saveTemplateValues(): void {
    if (!this.templateColumns.length || !this.dataChanged || this.saving) {
      return;
    }
    this.saving = true;
    // Parallel-save with forkJoin so we get a single deterministic completion point. The
    // previous implementation incremented/decremented a `pendingSaves` counter inside each
    // subscription's `next`/`error` callback and triggered the post-save reload from inside the
    // `next` block when pendingSaves hit 0. That had two bugs:
    //   (1) on partial failure, the reload could still fire from a successful save's callback
    //       before the failing observable's error callback ran, racing the reload against the
    //       error toast and leaving stale UI state;
    //   (2) the success toast and reload could fire in any interleaved order across columns,
    //       causing the inventory reload to run multiple times during quick reorderings of
    //       observable completion.
    // Using forkJoin we collect per-save outcomes (success/failure) in one place, fire exactly
    // one reload after all saves finish, and surface successes / failures with deterministic
    // ordering. catchError swallows per-save errors into outcome objects so forkJoin still
    // resolves even if some saves fail; the aggregate handler then decides whether to reload.
    const columns = [...this.templateColumns];
    const requests = columns.map(templateColumn => {
      const rows = this.templateRows
        .filter((row: any) => this.hasAssignedConnectorAndPin(row))
        .filter((row: any) => row?.state !== 'deleted')
        .map((row: any) => ({
          fkIoMatrixRowId: (row?.targetIoMatrixRowId ?? row?.id) as number,
          icdCircuitSelection: this.normalizeTemplateCircuitSelection(row[templateColumn.field])
        }));
      return this.esciService.saveIcdTemplateValues(
        normalizeIoMatrixScopeField(this.objectCode),
        normalizeIoMatrixScopeField(this.objectCodeInst),
        normalizeIoMatrixScopeField(this.architecture),
        normalizeIoMatrixScopeField(this.cadenceVer),
        templateColumn.program,
        templateColumn.modelYear,
        templateColumn.rpo,
        templateColumn.additionalRpos,
        templateColumn.buildPhase,
        this.getSelectedTemplateVariant(),
        rows
      ).pipe(
        map(() => ({column: templateColumn, ok: true, err: null as any})),
        catchError(err => of({column: templateColumn, ok: false, err}))
      );
    });

    forkJoin(requests).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: outcomes => {
        try {
          outcomes.forEach(outcome => {
            const c = outcome.column;
            if (outcome.ok) {
              this.messageService.processSuccessWithoutClear(
                `Saved ${c.program} ${c.modelYear} ${c.rpo} ${c.buildPhase}.`
              );
            } else {
              this.messageService.processError(outcome.err?.error?.message || 'Failed to save template values.');
            }
          });
          const allOk = outcomes.every(outcome => outcome.ok);
          if (allOk) {
            this.initializeTemplateChangeTracking();
            this.inlineMergeOriginalValueDisplay = false;
            this.cadenceMergePreviewActive = false;
            this.cadenceMergeSourceCadenceVer = '';
            this.reloadWorkflowContextAfterTemplateSave();
          }
        } finally {
          this.saving = false;
        }
      },
      error: (err) => {
        // Defensive: catchError above should make this unreachable, but guarantee we clear saving.
        this.saving = false;
        this.messageService.processError(err?.error?.message || 'Failed to save template values.');
      }
    });
  }

  runTemplateCadenceUpgrade(): void {
    const selected = this.resolveSelectedMatrixSnapshot();
    const selectedTemplate = this.resolveSelectedExistingTemplate();
    if (!selected || !selectedTemplate || !this.templateTargetCadenceVer) {
      this.messageService.processError('No newer IO Matrix cadence is available for template merge.');
      return;
    }
    const targetSnapshot = this.savedMatrixOptions.find(option =>
      option.snapshot.matrixId === selected.matrixId && option.snapshot.cadenceVer === this.templateTargetCadenceVer
    )?.snapshot;
    if (!targetSnapshot) {
      this.messageService.processError('Target IO Matrix cadence snapshot was not found.');
      return;
    }
    this.esciService.getIoMatrixRows(targetSnapshot.matrixId, targetSnapshot.cadenceVer).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: response => {
        const mergedRows: any[] = this.buildCadenceMergeRows(response?.rows || [], this.ioMatrixRows || []) as any[];
        const sourceRowsById = new Map<number, any>((this.templateRows || [])
          .map((row: any) => [row?.id as number, row]));
        const templateFields = this.templateColumns.map(column => column.field);
        const mergedWithTemplateValues = mergedRows.map((row: any) => {
          const sourceRow = sourceRowsById.get(row?.id as number);
          if (!sourceRow) {
            return row;
          }
          const hydrated = {...row};
          templateFields.forEach(field => {
            hydrated[field] = sourceRow[field] ?? '';
          });
          return hydrated;
        });
        this.setIoMatrixRows(mergedWithTemplateValues as any);
        this.templateRows = structuredClone(this.ioMatrixRows as any);
        this.cadenceMergePreviewActive = true;
        this.inlineMergeOriginalValueDisplay = true;
        this.cadenceMergeSourceCadenceVer = selected.cadenceVer || '';
        this.cadenceVer = targetSnapshot.cadenceVer || '';
        this.selectedMatrixKey = `${targetSnapshot.matrixId}|${targetSnapshot.cadenceVer}`;
        this.syncSelectedMatrixLabelFromKey();
        this.templateCadenceUpgradeAvailable = false;
        this.templateLatestCadenceAlreadyExistsWarning = false;
        this.templateTargetCadenceVer = '';
        // Keep save/reset enabled so users can persist the cadence copy even when merge rows are unchanged.
        this.dataChanged = true;
        if (this.isGridApiAlive(this.gridApi)) {
          this.gridApi.refreshCells({force: true});
          this.gridApi.redrawRows();
        }
        this.messageService.processSuccess('Template cadence merge rows loaded for review.');
      },
      error: (err) => this.messageService.processError(err?.error?.message || 'Template cadence merge failed.')
    });
  }

  get showTemplateCadenceMergeAction(): boolean {
    return this.templateMode === 'edit'
      && !!this.selectedExistingTemplateKey
      && this.templateColumns.length > 0
      && this.templateCadenceUpgradeAvailable;
  }

  private addColumnFromExistingTemplate(): void {
    const selectedTemplate = this.resolveSelectedExistingTemplate();
    if (!selectedTemplate) {
      this.messageService.processError('Select an existing ICD template.');
      return;
    }

    const program = selectedTemplate.program || '';
    const modelYear = selectedTemplate.modelYear || '';
    const rpo = selectedTemplate.rpo || '';
    const additionalRpos = selectedTemplate.additionalRpos || '';
    const buildPhase = selectedTemplate.buildPhase || '';
    const variant = this.getSelectedTemplateVariant();
    const field = this.toTemplateField(program, modelYear, rpo, additionalRpos, buildPhase, variant);
    const templateId = selectedTemplate.id ?? selectedTemplate.templateId;
    if (!templateId) {
      this.messageService.processError('Selected template id is invalid.');
      return;
    }
    if (!this.templateColumns.some(c => c.field === field)) {
      this.templateColumns.push({field, program, modelYear, rpo, additionalRpos, buildPhase, variant});
      this.rebuildTemplateColumns();
    }
    if (this.applyTemplateValuesFromCache(templateId, field)) {
      this.initializeTemplateChangeTracking();
      return;
    }

    this.esciService.getIcdTemplateDetail(templateId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (detail: any) => {
        this.cacheTemplateDetail(templateId, detail);
        this.applyTemplateValuesFromCache(templateId, field);
        this.initializeTemplateChangeTracking();
      },
      error: () => this.messageService.processError('Failed to load template detail.')
    });
  }

  private addColumnForNewTemplate(): void {
    if (!this.selectedProgramForNewTemplate || !this.selectedModelYearForNewTemplate
      || !this.selectedRpoForNewTemplate || !this.selectedBuildPhaseForNewTemplate) {
      this.messageService.processError('Select Program, Model Year, RPO, and Build Phase.');
      return;
    }
    const existingColumnCount = this.templateColumns.length;
    this.selectedProgram = this.selectedProgramForNewTemplate;
    this.selectedModelYear = this.selectedModelYearForNewTemplate;
    this.metadata['rpo'] = this.selectedRpoForNewTemplate;
    this.metadata['additionalRpos'] = this.selectedAdditionalRposForNewTemplate;
    this.metadata['buildPhase'] = this.selectedBuildPhaseForNewTemplate;
    this.addProgramModelYearColumn();
    if (this.templateColumns.length > existingColumnCount) {
      this.selectedProgramForNewTemplate = '';
      this.selectedModelYearForNewTemplate = '';
      this.selectedRpoForNewTemplate = '';
      this.selectedAdditionalRposForNewTemplate = '';
      this.selectedBuildPhaseForNewTemplate = '';
      this.programQuerySignal.set('');
      this.dataChanged = true;
    }
  }

  protected override rebuildTemplateColumns(): void {
    super.rebuildTemplateColumns();
  }

  private stripTemplateColumnValues(row: any): any {
    const sanitizedRow: any = {...(row || {})};
    Object.keys(sanitizedRow).forEach(key => {
      if (key.startsWith('template_')) {
        delete sanitizedRow[key];
      }
    });
    return sanitizedRow;
  }

  private initializeTemplateChangeTracking(): void {
    this.templateRows = this.prepareTemplateRowsForTracking(this.templateRows || []);
    this.templateColumnsBaseline = structuredClone(this.templateColumns || []);
    this.templateRowsBaseline = structuredClone((this.templateRows || []).map((row: any) => row.originalRowData || row));
    this.dataChanged = false;
    this.duplicate = false;
    this.rowsInvalid = false;
  }

  private prepareTemplateRowsForTracking(rows: any[]): any[] {
    return (rows || []).map((row: any) => {
      const normalizedRow: any = {
        ...row,
        changes: new Map<string, string>(),
        state: 'saved'
      };
      normalizedRow.originalRowData = structuredClone({
        ...normalizedRow,
        changes: undefined,
        originalRowData: undefined
      });
      return normalizedRow;
    });
  }

  private normalizeTemplateInventoryRows(rows: IcdTemplateInventory[] | null | undefined): IcdTemplateInventory[] {
    return (rows || []).map(row => normalizeIcdTemplateInventory(row));
  }

  private reloadWorkflowContextAfterTemplateSave(): void {
    forkJoin({
      snapshots: this.esciService.getIoMatrixSnapshots().pipe(
        catchError(() => of([] as IoMatrixSnapshot[]))
      ),
      templates: this.esciService.getIcdTemplateInventory().pipe(
        catchError(() => of([] as IcdTemplateInventory[]))
      )
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: ({snapshots, templates}) => {
        this.savedMatrixOptions = buildIoMatrixSnapshotOptions(snapshots);
        this.templateInventory = this.normalizeTemplateInventoryRows(templates);
        this.syncSelectedMatrixKeyFromResolvedSnapshot();
        this.syncSelectedMatrixLabelFromKey();
        this.refreshTemplateSelectionOptions();
      }
    });
  }

  /**
   * Resolves the saved IO Matrix snapshot for the current selection. Prefer the dropdown key,
   * then match by OC/OCI/architecture/cadence (handles matrix_id key drift after save), then
   * fall back to the live matrix context signals when snapshots are still loading.
   */
  private resolveSelectedMatrixSnapshot(): IoMatrixSnapshot | undefined {
    if (this.selectedMatrixKey) {
      const byKey = this.savedMatrixOptions
        .find(option => option.key === this.selectedMatrixKey)?.snapshot;
      if (byKey) {
        return byKey;
      }
    }
    const cadence = (this.cadenceVer || '').trim();
    const byContext = this.savedMatrixOptions.find(option => {
      const snapshot = option.snapshot;
      return normalizeIoMatrixScopeField(snapshot.objectCode) === normalizeIoMatrixScopeField(this.objectCode)
        && normalizeIoMatrixScopeField(snapshot.objectCodeInst) === normalizeIoMatrixScopeField(this.objectCodeInst)
        && normalizeIoMatrixScopeField(snapshot.architecture) === normalizeIoMatrixScopeField(this.architecture)
        && normalizeIoMatrixScopeField(snapshot.cadenceVer) === cadence;
    })?.snapshot;
    if (byContext) {
      return byContext;
    }
    if (this.objectCode && this.architecture && this.hasObjectCodeInstSelection) {
      return {
        matrixId: this.matrixId() || (this.selectedMatrixKey.split('|')[0] || ''),
        objectCode: this.objectCode,
        objectCodeInst: normalizeIoMatrixScopeField(this.objectCodeInst),
        architecture: this.architecture,
        cadenceVer: cadence,
        rowCount: 0,
        inUse: false
      };
    }
    return undefined;
  }

  private syncSelectedMatrixKeyFromResolvedSnapshot(): void {
    const snapshot = this.resolveSelectedMatrixSnapshot();
    if (!snapshot?.matrixId) {
      return;
    }
    this.selectedMatrixKey = `${snapshot.matrixId}|${snapshot.cadenceVer || ''}`;
  }

  private refreshTemplateSelectionOptions(): void {
    const selected = this.resolveSelectedMatrixSnapshot();
    if (!selected) {
      this.editableTemplateOptions = [];
      this.selectedExistingTemplateLabel = '';
      return;
    }

    const matrixTemplates = this.templateInventory.filter((template: IcdTemplateInventory) =>
      normalizeIoMatrixScopeField(template.objectCode) === normalizeIoMatrixScopeField(selected.objectCode)
      && normalizeIoMatrixScopeField(template.objectCodeInst) === normalizeIoMatrixScopeField(selected.objectCodeInst)
      && normalizeIoMatrixScopeField(template.architecture) === normalizeIoMatrixScopeField(selected.architecture)
      && normalizeIoMatrixScopeField(template.cadenceVer) === normalizeIoMatrixScopeField(selected.cadenceVer)
      && this.normalizeTemplateVariant(template.variant) === this.getSelectedTemplateVariant()
    );

    this.editableTemplateOptions = matrixTemplates.map(template => {
      const templateId = template.id ?? template.templateId;
      const variantLabel = this.normalizeTemplateVariant(template.variant)
        ? this.normalizeTemplateVariant(template.variant)
        : 'Original';
      return {
        label: `${template.program} ${template.modelYear} ${template.rpo} ${template.buildPhase} | ${variantLabel} | ${formatIoMatrixScopeLabel(
          template.objectCode,
          template.objectCodeInst,
          template.architecture,
          template.cadenceVer
        )}`,
        value: this.toTemplateOptionKey(templateId, template.cadenceVer || '', template.variant || '')
      };
    });
    if (this.selectedExistingTemplateKey
      && !this.editableTemplateOptions.some(option => option.value === this.selectedExistingTemplateKey)) {
      this.selectedExistingTemplateKey = null;
      this.selectedExistingTemplateLabel = '';
      this.existingTemplateQuerySignal.set('');
      this.templateCadenceUpgradeAvailable = false;
      this.templateLatestCadenceAlreadyExistsWarning = false;
      this.templateTargetCadenceVer = '';
    }
    this.syncSelectedExistingTemplateLabelFromKey();

    this.prefetchTemplateDetails(matrixTemplates
      .map(template => template.id ?? template.templateId)
      .filter((id): id is number => Number.isFinite(id)));
  }

  private loadProgramAndModelYearReferenceOptions(): void {
    this.esciService.getAllowedIoMatrixProgramOptions().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: options => {
        this.programOptionsSignal.set(options || []);
        this.programQuerySignal.set('');
      },
      error: () => {
        this.programOptionsSignal.set([]);
        this.programQuerySignal.set('');
        this.messageService.processError('Failed to load Program reference options.');
      }
    });
    // Use the standard ICD model year range from 2020-2050.
    this.modelYearOptions = Array.from({length: 31}, (v, k) => (2020 + k).toString());
  }

  private clearMatrixContext(): void {
    this.allMatrixRowsSignal.set([]);
    this.allConnectorRowsSignal.set([]);
    this.selectedVariant = IO_MATRIX_ORIGINAL_VARIANT;
    this.objectCode = '';
    this.objectCodeInst = '';
    this.setObjectCodeInstSelected(false);
    this.architecture = '';
    this.cadenceVer = '';
    this.ioMatrixRows = [];
    this.templateRows = [];
    this.templateColumns = [];
    this.rebuildTemplateColumns();
    this.templateMode = null;
    this.selectedExistingTemplateKey = null;
    this.selectedExistingTemplateLabel = '';
    this.editableTemplateOptions = [];
    this.selectedMatrixLabel = '';
    this.savedMatrixQuerySignal.set('');
    this.existingTemplateQuerySignal.set('');
    this.programQuerySignal.set('');
    this.templateColumnsBaseline = [];
    this.templateRowsBaseline = [];
    this.templateDetailCache.clear();
    this.templateDetailLoading.clear();
    this.dataChanged = false;
    this.duplicate = false;
    this.rowsInvalid = false;
    this.templateCadenceUpgradeAvailable = false;
    this.templateLatestCadenceAlreadyExistsWarning = false;
    this.templateDeviceIoUpgradeWarning = false;
    this.templateTargetCadenceVer = '';
    this.latestMatrixCadenceVer = '';
    this.inlineMergeOriginalValueDisplay = false;
    this.cadenceMergePreviewActive = false;
    this.cadenceMergeSourceCadenceVer = '';
    this.refMapWarning = false;
  }

  private filterRowsWithAssignedConnectorAndPin(rows: any[]): any[] {
    return (rows || []).filter((row: any) => this.hasAssignedConnectorAndPin(row));
  }

  private hasAssignedConnectorAndPin(row: any): boolean {
    return !!String(row?.connectorAndPin || '').trim();
  }

  private syncSelectedMatrixLabelFromKey(): void {
    if (!this.selectedMatrixKey) {
      this.selectedMatrixLabel = '';
      return;
    }
    this.selectedMatrixLabel = this.savedMatrixOptions
      .find(option => option.key === this.selectedMatrixKey)?.label || '';
  }

  private syncSelectedExistingTemplateLabelFromKey(): void {
    if (!this.selectedExistingTemplateKey) {
      this.selectedExistingTemplateLabel = '';
      return;
    }
    this.selectedExistingTemplateLabel = this.editableTemplateOptions
      .find(option => option.value === this.selectedExistingTemplateKey)?.label || '';
  }

  private evaluateCadenceUpgradeSignals(selected: IoMatrixSnapshot, selectedTemplate?: IcdTemplateInventory): void {
    const sameMatrixSnapshots = (this.savedMatrixOptions || [])
      .map(option => option.snapshot)
      .filter(snapshot => snapshot.matrixId === selected.matrixId);
    const latestSaved = this.pickLatestCadence(sameMatrixSnapshots.map(snapshot => snapshot.cadenceVer || ''));
    this.latestMatrixCadenceVer = latestSaved;
    const selectedTemplateCadenceVer = selectedTemplate?.cadenceVer || '';
    const templateAlreadyExistsAtLatestCadence = !!selectedTemplate && !!latestSaved
      && this.templateInventory.some(template => this.isSameTemplateIdentity(template, selectedTemplate)
        && (template.cadenceVer || '') === latestSaved);
    this.templateCadenceUpgradeAvailable = !!selectedTemplateCadenceVer
      && !!latestSaved
      && latestSaved !== selectedTemplateCadenceVer
      && !templateAlreadyExistsAtLatestCadence;
    this.templateLatestCadenceAlreadyExistsWarning = !!selectedTemplateCadenceVer
      && !!latestSaved
      && latestSaved !== selectedTemplateCadenceVer
      && templateAlreadyExistsAtLatestCadence;
    this.templateTargetCadenceVer = this.templateCadenceUpgradeAvailable ? latestSaved : '';

    this.esciService.getIoMatrixSourceRows(selected.objectCode, selected.objectCodeInst, selected.architecture).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: sourceRows => {
        const latestDeviceIoCadence = this.latestSourceCadenceFromRows(sourceRows || []);
        this.templateDeviceIoUpgradeWarning = !!latestDeviceIoCadence
          && !!latestSaved
          && latestDeviceIoCadence !== latestSaved;
      },
      error: () => {
        this.templateDeviceIoUpgradeWarning = false;
      }
    });
  }

  private resolveSelectedExistingTemplate(): IcdTemplateInventory | undefined {
    if (!this.selectedExistingTemplateKey) {
      return undefined;
    }
    return this.templateInventory.find(template =>
      this.toTemplateOptionKey(
        template.id ?? template.templateId,
        template.cadenceVer || '',
        template.variant || ''
      ) === this.selectedExistingTemplateKey
    );
  }

  private toTemplateOptionKey(templateId: number | undefined, cadenceVer: string, variant: string): string {
    return `${templateId || ''}|${cadenceVer || ''}|${this.normalizeTemplateVariant(variant)}`;
  }

  private isSameTemplateIdentity(candidate: IcdTemplateInventory, selectedTemplate: IcdTemplateInventory): boolean {
    return (candidate.objectCode || '') === (selectedTemplate.objectCode || '')
      && (candidate.objectCodeInst || '') === (selectedTemplate.objectCodeInst || '')
      && (candidate.architecture || '') === (selectedTemplate.architecture || '')
      && (candidate.program || '') === (selectedTemplate.program || '')
      && (candidate.modelYear || '') === (selectedTemplate.modelYear || '')
      && (candidate.rpo || '') === (selectedTemplate.rpo || '')
      && (candidate.additionalRpos || '') === (selectedTemplate.additionalRpos || '')
      && (candidate.buildPhase || '') === (selectedTemplate.buildPhase || '')
      && this.normalizeTemplateVariant(candidate.variant) === this.normalizeTemplateVariant(selectedTemplate.variant);
  }

  private applySelectedVariantToRows(): void {
    const filtered = this.applyVariantFilter(this.allConnectorRowsSignal(), this.selectedVariant);
    this.ioMatrixRows = this.prepareTemplateRowsForTracking(filtered);
    this.templateRows = structuredClone(this.ioMatrixRows);
    if (this.isGridApiAlive(this.gridApi)) {
      const api: any = this.gridApi as any;
      if (typeof api.setGridOption === 'function') {
        api.setGridOption('rowData', this.templateRows);
      } else if (typeof api.setRowData === 'function') {
        api.setRowData(this.templateRows);
      }
      this.gridApi.refreshCells({force: true});
    }
  }

  private clearTemplateColumnsOnVariantChange(): void {
    if (!this.templateColumns.length && !this.templateColumnsBaseline.length && !this.dataChanged) {
      return;
    }
    this.templateColumns = [];
    this.templateRows = this.allConnectorRowsSignal().length
      ? this.prepareTemplateRowsForTracking(this.applyVariantFilter(this.allConnectorRowsSignal(), this.selectedVariant))
      : [];
    this.rebuildTemplateColumns();
    this.templateColumnsBaseline = [];
    this.templateRowsBaseline = [];
    this.dataChanged = false;
    this.duplicate = false;
    this.rowsInvalid = false;
    this.selectedExistingTemplateKey = null;
    this.selectedExistingTemplateLabel = '';
    this.existingTemplateQuerySignal.set('');
  }

  private prefetchTemplateDetails(templateIds: number[]): void {
    for (const templateId of templateIds || []) {
      if (!templateId || this.templateDetailCache.has(templateId) || this.templateDetailLoading.has(templateId)) {
        continue;
      }
      this.templateDetailLoading.add(templateId);
      this.esciService.getIcdTemplateDetail(templateId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: detail => this.cacheTemplateDetail(templateId, detail),
        error: () => {
          // Keep UI responsive; users can still trigger on-demand load when adding a column.
        },
        complete: () => this.templateDetailLoading.delete(templateId)
      });
    }
  }

  private cacheTemplateDetail(templateId: number, detail: any): void {
    const detailRows = detail?.ioMatrixIcdTemplatePins || detail?.rows || [];
    const valueByRowId = new Map<number, string>(
      detailRows.map((row: any) => [
        row?.ioMatrixRowId ?? row?.fkIoMatrixRowId ?? -1,
        this.normalizeTemplateCircuitSelection(row?.icdCircuitSelection)
      ])
    );
    this.templateDetailCache.set(templateId, valueByRowId);
  }

  private applyTemplateValuesFromCache(templateId: number, field: string): boolean {
    const valueByRowId = this.templateDetailCache.get(templateId);
    if (!valueByRowId) {
      return false;
    }
    this.templateRows = this.templateRows.map(row => ({
      ...row,
      [field]: valueByRowId.get(row.id ?? -1) || ''
    }));
    return true;
  }

  filterProgramSuggestions(event: { query?: string }): void {
    this.programQuerySignal.set(event?.query ?? '');
  }

  onProgramSelected(): void {
    this.programQuerySignal.set(this.selectedProgramForNewTemplate || '');
  }

  onProgramCleared(): void {
    this.selectedProgramForNewTemplate = '';
    this.selectedRpoForNewTemplate = '';
    this.selectedAdditionalRposForNewTemplate = '';
    this.selectedBuildPhaseForNewTemplate = '';
    this.programQuerySignal.set('');
  }

    get filteredProgramOptions(): string[] {
        const query = (this.programQuerySignal() || '').trim().toLowerCase();
        const allPrograms = this.programOptionsSignal();
        if (!query) {
            return allPrograms;
        }
        return allPrograms.filter(program => (program || '').toLowerCase().includes(query));
    }

  openCopyTemplateDialog(): void {
    if (!this.templateColumns.length) {
      return;
    }
    this.copyDialogVisible = true;
    this.copySourceColumnField = this.templateColumns.length === 1 ? this.templateColumns[0].field : '';
    this.copyTargetProgram = '';
    this.copyTargetModelYear = '';
    this.copyTargetRpo = '';
    this.copyTargetAdditionalRpos = '';
    this.copyTargetBuildPhase = '';
    this.copyTargetProgramQuerySignal.set('');
  }

  closeCopyTemplateDialog(): void {
    this.copyDialogVisible = false;
    this.copySourceColumnField = '';
    this.copyTargetProgram = '';
    this.copyTargetModelYear = '';
    this.copyTargetRpo = '';
    this.copyTargetAdditionalRpos = '';
    this.copyTargetBuildPhase = '';
    this.copyTargetProgramQuerySignal.set('');
  }

  executeCopyTemplate(): void {
    if (!this.copyDialogValid) {
      this.messageService.processError('Select a source template and target Program, Model Year, RPO, and Build Phase.');
      return;
    }
    const sourceColumn = this.templateColumns.find(column => column.field === this.copySourceColumnField);
    if (!sourceColumn) {
      this.messageService.processError('Source template column was not found.');
      return;
    }
    const targetProgram = (this.copyTargetProgram || '').trim();
    const targetModelYear = (this.copyTargetModelYear || '').trim();
    const targetRpo = (this.copyTargetRpo || '').trim();
    const targetAdditionalRpos = (this.copyTargetAdditionalRpos || '').trim();
    const targetBuildPhase = (this.copyTargetBuildPhase || '').trim();
    const targetField = this.toTemplateField(
      targetProgram,
      targetModelYear,
      targetRpo,
      targetAdditionalRpos,
      targetBuildPhase,
      this.getSelectedTemplateVariant()
    );
    if (this.templateColumns.some(column => column.field === targetField)) {
      this.messageService.processError('A column with that Program / Model Year / RPO / Additional RPOs / Build Phase already exists in the grid.');
      return;
    }
    if (this.targetMatchesExistingInventoryTemplate(targetField)) {
      this.messageService.processError('A template with that Program / Model Year / RPO / Additional RPOs / Build Phase already exists for this IO Matrix. Pick different values.');
      return;
    }
    this.templateColumns.push({
      field: targetField,
      program: targetProgram,
      modelYear: targetModelYear,
      rpo: targetRpo,
      additionalRpos: targetAdditionalRpos,
      buildPhase: targetBuildPhase,
      variant: this.getSelectedTemplateVariant()
    });
    for (const row of this.templateRows) {
      row[targetField] = row[sourceColumn.field] ?? '';
    }
    this.rebuildTemplateColumns();
    this.dataChanged = true;
    this.closeCopyTemplateDialog();
  }

  filterCopyTargetProgramSuggestions(event: { query?: string }): void {
    this.copyTargetProgramQuerySignal.set(event?.query ?? '');
  }

  get copyDialogValid(): boolean {
    return !!this.copySourceColumnField
      && !!(this.copyTargetProgram || '').trim()
      && !!(this.copyTargetModelYear || '').trim()
      && !!(this.copyTargetRpo || '').trim()
      && !!(this.copyTargetBuildPhase || '').trim();
  }

  get copyTemplateSourceOptions(): { label: string; value: string }[] {
    return (this.templateColumns || []).map(column => ({
      label: this.buildCopyTemplateColumnLabel(column),
      value: column.field
    }));
  }

  get copyTemplateSourceLabel(): string {
    const column = (this.templateColumns || []).find(c => c.field === this.copySourceColumnField);
    return column ? this.buildCopyTemplateColumnLabel(column) : '';
  }

  get filteredCopyTargetProgramOptions(): string[] {
    const query = (this.copyTargetProgramQuerySignal() || '').trim().toLowerCase();
    const allPrograms = this.programOptionsSignal();
    if (!query) {
      return allPrograms;
    }
    return allPrograms.filter(program => (program || '').toLowerCase().includes(query));
  }

  private buildCopyTemplateColumnLabel(column: { program: string; modelYear: string; rpo: string; additionalRpos: string; buildPhase: string }): string {
    const additionalSegment = column.additionalRpos ? ` ${column.additionalRpos}` : '';
    return `${column.program} ${column.modelYear} ${column.rpo}${additionalSegment} ${column.buildPhase}`;
  }

  private targetMatchesExistingInventoryTemplate(targetField: string): boolean {
    const matrixTemplates = (this.templateInventory || []).filter((template: IcdTemplateInventory) =>
      (template.objectCode || '') === (this.objectCode || '')
      && (template.objectCodeInst || '') === (this.objectCodeInst || '')
      && (template.architecture || '') === (this.architecture || '')
      && (template.cadenceVer || '') === (this.cadenceVer || '')
      && this.normalizeTemplateVariant(template.variant) === this.getSelectedTemplateVariant()
    );
    return matrixTemplates.some((template: IcdTemplateInventory) => this.toTemplateField(
      template.program || '',
      template.modelYear || '',
      template.rpo || '',
      template.additionalRpos || '',
      template.buildPhase || '',
      template.variant || ''
    ) === targetField);
  }
}

